/*----------------------------------
- NEWSLETTER SUBSCRIBE FORM
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import type { EditionPersona } from '@uniquedomains/components/contracts/editions/public';
import Icon from '@/client/components/ui/icon/Icon';
import { InlineSpinner, PendingLabel } from '@uniquedomains/components/ui/spinner';

import { NEWSLETTER_HONEYPOT_FIELD, type NewsletterPublicSubscriptionSource } from '@uniquedomains/components/contracts/newsletter/public';

import {
    useNewsletterSubscribe,
    type NewsletterFormStatus,
} from './useNewsletterSubscribe';

/*----------------------------------
- TYPES
----------------------------------*/

type NewsletterSubscribeFormSize = 'sm' | 'md' | 'lg' | 'xl';

type NewsletterSubscribeFormProps = {
    source: NewsletterPublicSubscriptionSource;
    /** The editions band asks the reader's persona (ADR-0074); every other form omits it. */
    persona?: EditionPersona;
    /**
     * Distinguishes two captures that claim the same source on one page, so the
     * field id, the label association and the test ids stay unique.
     */
    instanceId?: string;
    /** `dark` sits on the ink bands, `light` on the page surface. */
    tone?: 'light' | 'dark';
    size?: NewsletterSubscribeFormSize;
    /** `inline` keeps field and button on one row, `stacked` puts the button below. */
    layout?: 'inline' | 'stacked';
    submitLabel?: string;
    showArrow?: boolean;
    /**
     * `pill` is the hero capture of the 2026-09 newsletter page: from `sm` one white pill
     * holds the field and an ink button that ends on the butter arrow circle; on a phone
     * the field and a full-width button stack. Light ground only. `split` is every other
     * capture: a field and a button side by side.
     */
    frame?: 'split' | 'pill';
    className?: string;
    /** Called once the list accepted the address (never with it), for a page's own analytics. */
    onSubscribed?: () => void;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/**
 * One confirmation for every accepted address. The endpoint never says whether an
 * address was already on the list, so the form has nothing else to show.
 */
const FEEDBACK_BY_STATUS: Record<NewsletterFormStatus, string> = {
    idle: '',
    submitting: '',
    subscribed: 'You are on the list. The next brief lands Monday at 07:00 UTC.',
    invalid: 'Enter a valid email address.',
    too_fast: 'That went through a little too quickly. Submit again to confirm.',
    rate_limited: 'Too many attempts. Try again in a minute.',
    error: 'Subscription is temporarily unavailable. Please try again shortly.',
};

// The `pill` frame. Below `sm` the row is a plain stack, so the field carries its own
// border; from `sm` the row is the pill and the field inside it goes borderless.
const PILL_ROW_CLASS =
    'flex flex-col gap-2 sm:flex-row sm:items-center sm:rounded-full sm:border sm:border-[#e6e2da] sm:bg-white sm:p-1.5 sm:shadow-[0_18px_44px_rgba(27,29,30,0.06)] sm:focus-within:border-[rgba(27,29,30,0.4)]';
const PILL_FIELD_CLASS =
    'h-[54px] w-full min-w-0 rounded-full border border-[#d9d5cc] bg-white px-[18px] text-base text-[#1b1d1e] outline-none transition placeholder:text-[#6a695e] focus:border-[rgba(27,29,30,0.4)] sm:h-[52px] sm:border-0 sm:bg-transparent';
const PILL_BUTTON_CLASS =
    'inline-flex h-14 shrink-0 items-center justify-between gap-3 rounded-full bg-[#1b1d1e] pl-[22px] pr-1.5 text-[15.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-70 sm:h-[52px] sm:text-[15px]';

const FIELD_HEIGHT: Record<NewsletterSubscribeFormSize, string> = {
    sm: 'h-[38px] text-[12.5px]',
    md: 'h-[46px] text-[13.5px]',
    lg: 'h-12 text-sm',
    // The `/newsletter` hero capture, the only 58px field in the mockup.
    xl: 'h-[58px] text-base',
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The one subscribe capture used by the `/newsletter` page, the Resources menu,
 * the news archive and the edition page. It lives in the Website app because it
 * posts to a Website-only gateway route, so it is composed around the shared
 * article, never inside it.
 */
export default function NewsletterSubscribeForm({
    source,
    instanceId,
    tone = 'light',
    size = 'md',
    layout = 'inline',
    submitLabel = 'Subscribe',
    showArrow = false,
    frame = 'split',
    className = '',
    onSubscribed,
    persona,
}: NewsletterSubscribeFormProps) {
    const { email, setEmail, honeypot, setHoneypot, status, submit } =
        useNewsletterSubscribe(source, onSubscribed, persona);

    const isDark = tone === 'dark';
    const isSettled = status === 'subscribed';
    const isError = status === 'invalid'
        || status === 'too_fast'
        || status === 'rate_limited'
        || status === 'error';
    const feedback = FEEDBACK_BY_STATUS[status];
    const instance = instanceId || source;
    const fieldId = `newsletter-email-${instance}`;
    const feedbackId = `${fieldId}-feedback`;

    const isPill = frame === 'pill';
    const submitting = status === 'submitting';
    // With no arrow to swap, the spinner is laid over the label so the button keeps its width.
    const coversLabel = submitting && !isPill && !showArrow;

    const fieldClass = isPill ? PILL_FIELD_CLASS : [
        'w-full min-w-0 rounded-full px-5 outline-none transition',
        FIELD_HEIGHT[size],
        isDark
            ? 'border border-white/[0.16] bg-white/[0.08] text-white placeholder:text-[#ffffff8c] focus:border-[#ffffff66]'
            : 'border border-[rgba(27,29,30,0.12)] bg-white text-[#1b1d1e] placeholder:text-[#6a695e] focus:border-[rgba(27,29,30,0.4)]',
    ].join(' ');

    const buttonClass = isPill ? PILL_BUTTON_CLASS : [
        'inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-5 font-bold transition disabled:cursor-not-allowed disabled:opacity-70',
        FIELD_HEIGHT[size],
        isDark
            ? // White on ink (2026-09 mockups): butter is kept for the arrow circles of the
              // primary CTAs, so a second butter fill on the same dark card would compete with them.
              'bg-white text-[#1b1d1e] hover:bg-[#f1ece6]'
            : 'bg-[#1b1d1e] text-white hover:bg-[#000000]',
    ].join(' ');

    if (isSettled) {
        return (
            <p
                role="status"
                data-testid={`newsletter-subscribe-confirmation-${instance}`}
                class={[
                    'text-[13.5px] leading-6',
                    isDark ? 'text-white/70' : 'text-[#4a4d4f]',
                    className,
                ].join(' ')}
            >
                {feedback}
            </p>
        );
    }

    return (
        <form
            class={['flex flex-col gap-2', className].join(' ')}
            aria-busy={status === 'submitting'}
            data-testid={`newsletter-subscribe-form-${instance}`}
            onSubmit={(event) => {
                event.preventDefault();
                void submit();
            }}
        >
            <div
                class={
                    isPill
                        ? PILL_ROW_CLASS
                        : ['flex gap-2', layout === 'stacked' ? 'flex-col' : 'flex-col sm:flex-row sm:items-center'].join(' ')
                }
            >
                <label class="sr-only" for={fieldId}>
                    Email address
                </label>
                <input
                    id={fieldId}
                    type="email"
                    name="email"
                    value={email}
                    required
                    maxLength={255}
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@company.com"
                    readOnly={status === 'submitting'}
                    aria-invalid={status === 'invalid' ? 'true' : undefined}
                    aria-describedby={isError ? feedbackId : undefined}
                    class={fieldClass}
                    onInput={(event) => setEmail((event.target as HTMLInputElement).value)}
                />

                {/*
                    Decoy field. The label and the name avoid every token a password
                    manager fills, because an autofill match here would silently drop
                    a real subscriber.
                */}
                <div class="ud-landing-newsletter-honeypot" aria-hidden="true">
                    <label for={`${fieldId}-${NEWSLETTER_HONEYPOT_FIELD}`}>
                        Leave this field empty
                    </label>
                    <input
                        id={`${fieldId}-${NEWSLETTER_HONEYPOT_FIELD}`}
                        type="text"
                        name={NEWSLETTER_HONEYPOT_FIELD}
                        value={honeypot}
                        tabIndex={-1}
                        autoComplete="off"
                        onInput={(event) => setHoneypot((event.target as HTMLInputElement).value)}
                    />
                </div>

                <button
                    type="submit"
                    class={[buttonClass, coversLabel ? 'relative' : ''].join(' ')}
                    disabled={submitting}
                    aria-busy={submitting || undefined}
                >
                    <PendingLabel pending={coversLabel}>{submitLabel}</PendingLabel>
                    {isPill ? (
                        <span
                            aria-hidden="true"
                            class="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#f2e97e] text-[#1b1d1e] sm:h-10 sm:w-10"
                        >
                            {submitting ? <InlineSpinner size={16} /> : <Icon name="arrow-right" size={16} strokeWidth={2.2} />}
                        </span>
                    ) : showArrow ? (
                        submitting ? <InlineSpinner size={15} /> : <Icon name="arrow-right" size={15} />
                    ) : null}
                </button>
            </div>

            {isError ? (
                <p
                    id={feedbackId}
                    role="alert"
                    class={[
                        'text-[12.5px] leading-5',
                        isDark ? 'text-white/70' : 'text-[#66696b]',
                    ].join(' ')}
                >
                    {feedback}
                </p>
            ) : null}
        </form>
    );
}
